import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useTranslate } from 'react-polyglot';
import styles from './ExternalStatistics.module.scss';
import backgroundImage from '../../../Assets/Images/background.svg';
import { ExternalLayoutContext } from '../ExternalLayout';
import BarGraphCard from '../../Pages/Statistics/Cards/BarGraphCard/BarGraphCard';

// The view returns one row per (breed, year, prize). Prize 0 means the dog
// started but took no prize, so it belongs on the chart as its own series.
// Stacked bottom-up in this order, which puts 1. premie on the flat baseline.
// Matches the rolling window in DOG.V_TEST_PRIZE_PER_YEAR.
const YEARS_SHOWN = 15;

const PRIZE_SERIES = [
    { prize: 1, color: '#68DB5A' },
    { prize: 2, color: '#FFE144' },
    { prize: 3, color: '#F89D27' },
    { prize: 0, color: '#A0AEC0' }
];

interface PrizeRow {
    BREED_ID: number;
    TEST_YEAR: number;
    PRIZE: number;
    N_RESULTS: number;
    PCT_OF_YEAR: number;
}

function ExternalStatistics() {
    const t = useTranslate();
    const [searchParams] = useSearchParams();
    const { breeds, setBackLink, setShowBreedDropdown } = useOutletContext<ExternalLayoutContext>();

    const [rows, setRows] = useState<PrizeRow[]>([]);
    const [selectedBreeds, setSelectedBreeds] = useState<number[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        setBackLink({ to: `/external?${searchParams.toString()}`, text: `← ${t("externalportal.page.backbutton")}` });
        // This page covers every breed, so the header's single-breed dropdown
        // would only be misleading here.
        setShowBreedDropdown(false);
        return () => {
            setBackLink(undefined);
            setShowBreedDropdown(true);
        };
    }, []);

    useEffect(() => {
        setLoading(true);
        axios.get('/api/statistics/prize/public/distributionyear')
            .then((res) => {
                const data: PrizeRow[] = res.data || [];
                setRows(data);
                // Everything on by default.
                setSelectedBreeds(Array.from(new Set(data.map(r => r.BREED_ID))));
            })
            .catch(() => { setRows([]); setSelectedBreeds([]); })
            .finally(() => setLoading(false));
    }, []);

    // Only the breeds the view actually has results for, named from the breed
    // list the layout already loaded.
    const availableBreeds = useMemo(() => {
        const ids = Array.from(new Set(rows.map(r => r.BREED_ID))).sort((a, b) => a - b);
        return ids.map(id => ({
            id,
            name: breeds.find((b: any) => Number(b.ID) === id)?.NAME ?? String(id)
        }));
    }, [rows, breeds]);

    function toggleBreed(breedId: number) {
        setSelectedBreeds(prev => prev.includes(breedId)
            ? prev.filter(i => i !== breedId)
            : [...prev, breedId]);
    }

    const data = useMemo(() => {
        const included = rows.filter(r => selectedBreeds.includes(r.BREED_ID));
        if (included.length === 0) return undefined;

        // The axis is always the view's full 15 year window, not just the years
        // the selected breeds happen to have results in. A breed with nothing
        // registered in a given year gets an empty column rather than having
        // the year disappear and the axis shift under it.
        const latestYear = rows.length > 0
            ? Math.max(...rows.map(r => r.TEST_YEAR))
            : new Date().getFullYear();
        const years = Array.from({ length: YEARS_SHOWN }, (_, i) => latestYear - (YEARS_SHOWN - 1) + i);

        // PCT_OF_YEAR is a share within one breed, so it cannot be summed or
        // averaged across breeds. Add up the counts first, then take the
        // percentage of the combined year total.
        const counts = new Map<number, Map<number, number>>(years.map(year => [year, new Map()]));
        const yearTotals = new Map<number, number>(years.map(year => [year, 0]));

        included.forEach(row => {
            const byPrize = counts.get(row.TEST_YEAR);
            if (byPrize == undefined) return;
            byPrize.set(row.PRIZE, (byPrize.get(row.PRIZE) ?? 0) + row.N_RESULTS);
            yearTotals.set(row.TEST_YEAR, (yearTotals.get(row.TEST_YEAR) ?? 0) + row.N_RESULTS);
        });

        return {
            labels: years,
            datasets: PRIZE_SERIES.map(serie => {
                // counts/totals are not chart.js options - they ride along on
                // the dataset so the tooltip can show the numbers behind the
                // percentage.
                const serieCounts = years.map(year => counts.get(year)?.get(serie.prize) ?? 0);
                const serieTotals = years.map(year => yearTotals.get(year) ?? 0);

                return {
                    label: serie.prize === 0
                        ? t("externalportal.statistics.prizedistribution.noprize")
                        : t("externalportal.statistics.prizedistribution.prize", { prize: serie.prize }),
                    backgroundColor: serie.color,
                    counts: serieCounts,
                    totals: serieTotals,
                    data: serieCounts.map((count, i) => serieTotals[i] === 0
                        ? 0
                        : Math.round((100 * count / serieTotals[i]) * 100) / 100)
                };
            })
        };
    }, [rows, selectedBreeds, t]);

    // Each year's shares add up to 100, so the bars are full-height and the
    // series can be read against each other rather than against the axis.
    const options = {
        scales: {
            x: { stacked: true },
            y: {
                stacked: true,
                min: 0,
                max: 100,
                ticks: { callback: (value: any) => `${value} %` }
            }
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: (context: any) => t("externalportal.statistics.prizedistribution.tooltip", {
                        label: context.dataset.label,
                        percent: context.parsed.y,
                        count: context.dataset.counts?.[context.dataIndex] ?? 0
                    }),
                    footer: (items: any[]) => items.length === 0 ? '' :
                        t("externalportal.statistics.prizedistribution.tooltiptotal", {
                            year: items[0].label,
                            total: items[0].dataset.totals?.[items[0].dataIndex] ?? 0
                        })
                }
            }
        }
    };

    return (
        <div
            className={styles.externalStatistics}
            style={{ '--background-image': `url(${backgroundImage})` } as React.CSSProperties}
        >
            <div className={styles.backgroundTail}></div>

            <div className={styles.mainContent}>
                <div className={styles.pageHeader}>
                    <div className={styles.headerRow}>
                        <h1>{t("externalportal.statistics.title")}</h1>
                    </div>
                </div>

                {availableBreeds.length > 0 &&
                    <div className={styles.breedFilter}>
                        <div className={styles.breedFilterHeader}>
                            <span className={styles.breedFilterTitle}>{t("externalportal.statistics.breedfilter.title")}</span>
                            <div className={styles.breedFilterActions}>
                                <button type="button" onClick={() => setSelectedBreeds(availableBreeds.map(b => b.id))}>
                                    {t("externalportal.statistics.breedfilter.selectall")}
                                </button>
                                <button type="button" onClick={() => setSelectedBreeds([])}>
                                    {t("externalportal.statistics.breedfilter.clear")}
                                </button>
                            </div>
                        </div>
                        <div className={styles.breedList}>
                            {availableBreeds.map(breed =>
                                <label key={breed.id} className={styles.breedOption}>
                                    <input
                                        type="checkbox"
                                        checked={selectedBreeds.includes(breed.id)}
                                        onChange={() => toggleBreed(breed.id)}
                                    />
                                    <span>{breed.name}</span>
                                </label>
                            )}
                        </div>
                    </div>
                }

                {loading ?
                    <p className={styles.message}>{t("externalportal.statistics.loading")}</p>
                : rows.length === 0 ?
                    <p className={styles.message}>{t("externalportal.statistics.nodata")}</p>
                : data == undefined ?
                    <p className={styles.message}>{t("externalportal.statistics.nobreedselected")}</p>
                :
                    <div className={styles.graphs}>
                        <BarGraphCard
                            title={t("externalportal.statistics.prizedistribution.title")}
                            data={data}
                            options={options}
                        />
                        <p className={styles.description}>
                            {t("externalportal.statistics.prizedistribution.description")}
                        </p>
                    </div>
                }
            </div>
        </div>
    );
}

export default ExternalStatistics;
